import type { ILanguageResources, ITranslationModule } from '../../../interfaces';
import { interpolate } from '../../../global';

// --- Localization IDs and Wrappers ---

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.scripts.quotes.v6.V6QuoteSupplierDefaultsView.';
  public static readonly branchQuoteDefaultsSubtitle = `${Id.prefix}branchQuoteDefaultsSubtitle`;
  public static readonly infoAttributesHeader = `${Id.prefix}infoAttributesHeader`;
  public static readonly infoQuoteDefaultsHeader = `${Id.prefix}infoQuoteDefaultsHeader`;
  public static readonly pleaseCloseQuoteItem = `${Id.prefix}pleaseCloseQuoteItem`;
  public static readonly save = `${Id.prefix}save`;
  public static readonly applyChanges = `${Id.prefix}applyChanges`;
  public static readonly applyToAllExistingFrame = `${Id.prefix}applyToAllExistingFrame`;
  public static readonly resetDefaults = `${Id.prefix}resetDefaults`;
  public static readonly supplierServiceNotAvailable = `${Id.prefix}supplierServiceNotAvailable`;
  public static readonly branchQuoteDefaultsTitle = `${Id.prefix}branchQuoteDefaultsTitle`;
  public static readonly saveChangesSuccess = `${Id.prefix}saveChangesSuccess`;
  public static readonly saveChangesError = `${Id.prefix}saveChangesError`;
}

export class LangV6QuoteSupplierDefaultsView {
  public static branchQuoteDefaultsSubtitle(): string {
    return interpolate(Id.branchQuoteDefaultsSubtitle);
  }
  private constructor() {}
  public static infoAttributesHeader(): string {
    return interpolate(Id.infoAttributesHeader);
  }
  public static infoQuoteDefaultsHeader(): string {
    return interpolate(Id.infoQuoteDefaultsHeader);
  }
  public static pleaseCloseQuoteItem(): string {
    return interpolate(Id.pleaseCloseQuoteItem);
  }
  public static applyChanges(): string {
    return interpolate(Id.applyChanges);
  }
  public static save(): string {
    return interpolate(Id.save);
  }
  public static applyToAllExistingFrame(): string {
    return interpolate(Id.applyToAllExistingFrame);
  }
  public static resetDefaults(): string {
    return interpolate(Id.resetDefaults);
  }
  public static supplierServiceNotAvailable(): string {
    return interpolate(Id.supplierServiceNotAvailable);
  }
  public static branchQuoteDefaultsTitle(): string {
    return interpolate(Id.branchQuoteDefaultsTitle);
  }
  public static saveChangesSuccess(): string {
    return interpolate(Id.saveChangesSuccess);
  }
  public static saveChangesError(): string {
    return interpolate(Id.saveChangesError);
  }
}

// --- Registration Module ---

export class V6QuoteSupplierDefaultsViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // Info markdown for attributes header
    register.add(
      Id.infoAttributesHeader,
      `## Information

Option changes may result in changes to items with {{glossary 'supplier'}} price adjustments <webmodule-icon library="fa" name="fas-tags" class="text-base"></webmodule-icon>, if so, these adjustments will be discarded.
You will then need to reactivate the associated support ticket to request a {{glossary 'supplier'}} review.`,
      'Information alert shown when supplier price adjustments may be discarded due to option changes (attributes header)'
    );

    // Info markdown for quote defaults header
    register.add(
      Id.infoQuoteDefaultsHeader,
      `## Information

Global option changes may result in changes to items with {{glossary 'supplier'}} price adjustments <webmodule-icon library="fa" name="fas-tags" class="text-base"></webmodule-icon>, if so, these adjustments will be discarded.
You will then need to reactivate the associated support ticket to request a {{glossary 'supplier'}} review.`,
      'Information alert shown when supplier price adjustments may be discarded due to global option changes (quote defaults header)'
    );

    // Please close any open quote-item first
    register.add(
      Id.pleaseCloseQuoteItem,
      "Please close any open {{glossary 'quote-item'}} first",
      'Prompt to close open quote items before applying changes'
    );

    // Apply Changes
    register.add(Id.applyChanges, 'Apply Changes', 'Button label for applying changes');

    // Apply To All Existing frame
    register.add(
      Id.applyToAllExistingFrame,
      "Apply To All Existing {{glossary 'frame:plural'}}",
      'Button label for applying changes to all existing frames'
    );

    // Reset Defaults
    register.add(Id.resetDefaults, 'Reset Defaults', 'Button label for resetting defaults');

    // supplier service not available
    register.add(
      Id.supplierServiceNotAvailable,
      "{{glossary 'supplier'}} service not available",
      'Message shown when supplier service is not available'
    );

    // branch quote defaults title
    register.add(Id.branchQuoteDefaultsTitle, 'Branch Quote Defaults', 'Title for branch quote defaults section');

    // branch quote defaults subtitle
    register.add(
      Id.branchQuoteDefaultsSubtitle,
      "These values will be applied to each new quote in the {{glossary 'branch'}} superceding the {{glossary 'supplier'}} defaults.",
      'Subtitle explaining branch quote defaults precedence'
    );
    register.add(Id.save, 'Save', 'Button label for saving changes');
    register.add(Id.saveChangesSuccess, 'Changes saved successfully', 'Success message when changes are saved');
    register.add(Id.saveChangesError, 'Failed to save changes', 'Error message when changes fail to save');
  }
}
